import React, { useState, useEffect, useRef, memo } from 'react';
import { BalloonEventModel, isVideoUrl, isYouTubeUrl, getYouTubeEmbedUrl, isInstagramUrl, getInstagramEmbedUrl, getImageUrl } from 'ts-core';

interface ImageOrPlaceholderProps {
    event: BalloonEventModel;
    size?: number;
    index?: number;
    className?: string;
    style?: React.CSSProperties;
    /**
     * Mark this image as the LCP candidate (the first slide). Sets
     * `fetchpriority="high"` + `loading="eager"` so the browser prioritises it.
     */
    priority?: boolean;
    /**
     * Hide the YouTube player chrome (controls, CC, settings, fullscreen) and
     * make the iframe non-interactive. Used by the compact list tiles where the
     * controls are crowded/unfunctional and the video should read as a thumbnail.
     */
    hideControls?: boolean;
}

function Skeleton({ width, height }: { width: string; height: string }) {
    return (
        <div
            className="bg-gray-200 rounded animate-pulse"
            style={{ width, height }}
        />
    );
}

/**
 * Renders an event image (or video) at a given index, with loading skeleton,
 * error fallback, and category-based placeholder when no images exist.
 *
 * Requires Swiper CSS to be imported by the consumer if used inside ImageSection.
 */
export const ImageOrPlaceholder: React.FC<ImageOrPlaceholderProps> = ({ event, className = "", size, index, style, priority, hideControls }) => {
    const i = index ?? 0;
    const [loaded, setLoaded] = useState(false);
    const [idx, setIdx] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        setIdx(0);
    }, [event]);

    const toggleMute = (e: React.MouseEvent) => {
        e.stopPropagation();
        const next = !isMuted;
        setIsMuted(next);
        if (videoRef.current) {
            videoRef.current.muted = next;
        }
    };

    const base = "https://be-res.s3.eu-central-1.amazonaws.com/images/placeholder";
    const tags = (event.tags ?? []).filter(Boolean);
    const cats = (event.categories ?? []).filter(Boolean).map(c => c.slug);
    const placeholders = [...cats, ...tags].filter(Boolean).map(s => `${base}/${encodeURIComponent(s)}.webp`);

    if (event.rawImages?.length) {
        const img = event.rawImages[i];
        const src = getImageUrl(img);
        const isVideo = isVideoUrl(img.url);
        const isYouTube = isYouTubeUrl(img.url);
        const embedUrl = isYouTube ? getYouTubeEmbedUrl(img.url, { controls: !hideControls }) : null;
        const isInstagram = isInstagramUrl(img.url);
        const igEmbedUrl = isInstagram ? getInstagramEmbedUrl(img.url) : null;
        return (
            <>
                {!loaded && <Skeleton width="100%" height="100%" />}
                {isInstagram && igEmbedUrl ? (
                    <div className={`absolute z-50 inset-0 flex items-center justify-center ${!loaded ? "hidden" : ""}`}>
                        {/* Contain (don't crop): the portrait reel card is centred at full
                            slide height with a capped width; the slide is made taller for IG. */}
                        <iframe
                            className="h-full w-full max-w-[360px] rounded-md bg-white drop-shadow-lg"
                            style={{ border: 0 }}
                            src={igEmbedUrl}
                            title={event.title}
                            scrolling="no"
                            allow="encrypted-media; picture-in-picture"
                            allowFullScreen
                            onLoad={() => setLoaded(true)}
                        />
                    </div>
                ) : isYouTube && embedUrl ? (
                    <iframe
                        className={`${className} ${!loaded ? "hidden" : ""}`}
                        style={{ ...style, height: style?.maxHeight ?? '100%', border: 0, ...(hideControls ? { pointerEvents: 'none' } : {}) }}
                        src={embedUrl}
                        title={event.title}
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen={!hideControls}
                        onLoad={() => setLoaded(true)}
                    />
                ) : isVideo ? (
                    <>
                        <video
                            ref={videoRef}
                            className={`cursor-pointer ${className} ${!loaded ? "hidden" : ""}`}
                            style={style}
                            src={src}
                            autoPlay
                            muted
                            loop
                            playsInline
                            onLoadedData={() => setLoaded(true)}
                            onError={() => setLoaded(true)}
                        />
                        {loaded && (
                            <button
                                onClick={toggleMute}
                                onPointerDown={(e) => e.stopPropagation()}
                                aria-label={isMuted ? "Unmute video" : "Mute video"}
                                className="absolute bottom-3 right-3 z-[999] flex items-center justify-center w-7 h-7 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                            >
                                {isMuted ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                        <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM17.78 9.22a.75.75 0 1 0-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 1 0 1.06-1.06L20.56 12l1.72-1.72a.75.75 0 1 0-1.06-1.06l-1.72 1.72-1.72-1.72Z" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                        <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM18.584 5.106a.75.75 0 0 1 1.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 0 1-1.06-1.06 8.25 8.25 0 0 0 0-11.668.75.75 0 0 1 0-1.06ZM15.932 7.757a.75.75 0 0 1 1.061 0 6 6 0 0 1 0 8.486.75.75 0 0 1-1.06-1.06 4.5 4.5 0 0 0 0-6.365.75.75 0 0 1-.001-1.061Z" />
                                    </svg>
                                )}
                            </button>
                        )}
                    </>
                ) : (
                    <img
                        className={`${className} ${!loaded ? "hidden" : ""}`}
                        style={style}
                        src={src}
                        alt={event.title}
                        fetchPriority={priority ? "high" : undefined}
                        loading={priority ? "eager" : undefined}
                        onLoad={() => setLoaded(true)}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = "https://res.balloon-events.com/logos/colored-notext.png";
                        }}
                    />
                )}
                <div className="size">{size}</div>
                {!isYouTube && !isInstagram && (
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70 pointer-events-none" />
                )}
            </>
        );
    }

    const src = placeholders.length > 0 ? placeholders[idx] ?? `${base}/other.webp` : `${base}/other.webp`;
    return (
        <>
            {!loaded && <Skeleton width="100%" height="100%" />}
            <img
                className={`${className} ${!loaded ? "hidden" : ""}`}
                style={style}
                src={src}
                alt={event.title}
                fetchPriority={priority ? "high" : undefined}
                loading={priority ? "eager" : undefined}
                onLoad={() => setLoaded(true)}
                onError={() => {
                    setLoaded(false);
                    setIdx(p => Math.min(p + 1, placeholders.length));
                }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80 pointer-events-none" />
        </>
    );
};

export const Placeholder = memo(function Placeholder(props: ImageOrPlaceholderProps) {
    const { event, size, className } = props;
    if (!event.categories || event.categories.length === 0) {
        return (
            <div className={`flex w-full h-full justify-center bg-red-100 items-center ${className}`}>
                <img
                    src="https://res.balloon-events.com/logos/colored-notext.png"
                    width={size}
                    alt="Fallback logo"
                />
            </div>
        );
    }
    return (
        <div
            className={`flex w-full h-full justify-center items-center ${!event.categories[0].color ? 'bg-red-100' : ''} ${className}`}
            style={{ backgroundColor: event.categories[0].color || '' }}
        >
            <div
                style={{
                    fontSize: size || 40,
                    fontFamily: 'fontAwesome',
                }}
                className={'flex items-center text-white justify-center ' + className}
            >
                {event.categories[0].icon}
            </div>
        </div>
    );
});
